import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  Sparkles, Check, Building2, Users, Calendar, BarChart3, 
  Shield, Zap, Globe, MessageSquare, ChevronRight, Star,
  ArrowRight, Phone, Mail, Lock, Heart, Clock, Award,
  Smartphone, TrendingUp, Headphones, CheckCircle, Play
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount);
};

// Hidden Super Admin Login Modal
// SuperAdminLoginModal removed - using dedicated /wtadmin route instead

export default function WellnessTechLanding({ onLogin }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [plans, setPlans] = useState([]);
  const [pricingData, setPricingData] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [keySequence, setKeySequence] = useState('');
  
  useEffect(() => {
    fetchPlans();
    
    // Hidden admin access - type "wtadmin" anywhere to redirect to admin login
    const handleKeyPress = (e) => {
      const newSequence = (keySequence + e.key).slice(-7);
      setKeySequence(newSequence);
      if (newSequence === 'wtadmin') {
        // Redirect to hidden admin login page
        window.location.href = '/wtadmin';
      }
    };
    
    window.addEventListener('keypress', handleKeyPress);
    return () => window.removeEventListener('keypress', handleKeyPress);
  }, [keySequence]);

  // Removed URL-based super admin access - now using /wtadmin route

  const fetchPlans = async () => {
    try {
      const res = await fetch(`${API_URL}/api/tenant/plans`);
      if (res.ok) {
        const data = await res.json();
        setPlans(data.plans || []);
        setPricingData(data);
      }
    } catch (error) {
      console.error('Failed to fetch plans:', error);
    }
  };

  const monthlyPlans = plans.filter(p => p.billing_cycle === 'monthly');
  
  // Calculate annual prices with discount
  const getDisplayPrice = (plan) => {
    const basePrice = plan.amount;
    const annualDiscount = pricingData?.annual_discount || 10;
    const specialDiscount = pricingData?.special_offer?.enabled ? pricingData.special_offer.discount_percent : 0;
    
    if (billingCycle === 'annual') {
      let annualPrice = basePrice * 12 * (100 - annualDiscount) / 100;
      if (specialDiscount > 0) {
        annualPrice = annualPrice * (100 - specialDiscount) / 100;
      }
      return Math.round(annualPrice);
    } else {
      if (specialDiscount > 0) {
        return Math.round(basePrice * (100 - specialDiscount) / 100);
      }
      return basePrice;
    }
  };
  
  const getOriginalPrice = (plan) => {
    if (billingCycle === 'annual') {
      return plan.original_amount * 12;
    }
    return plan.original_amount;
  };
  
  const trialDays = pricingData?.trial_days || 7;

  const features = [
    { icon: Calendar, title: 'Smart Booking System', desc: 'Interactive room heatmaps, drag-drop scheduling, package bookings with auto-allocation' },
    { icon: Users, title: 'Staff Management', desc: 'Attendance tracking, leave management, performance analytics, incentive calculations' },
    { icon: Building2, title: 'Multi-Location Support', desc: 'Manage unlimited wellness locations from a single dashboard with property-wise analytics' },
    { icon: BarChart3, title: 'Business Intelligence', desc: 'Real-time revenue dashboards, customer insights, forecasting, and trend analysis' },
    { icon: MessageSquare, title: 'WhatsApp & Email', desc: 'Automated booking confirmations, reminders, feedback requests, and marketing campaigns' },
    { icon: Shield, title: 'Role-Based Access', desc: 'Secure admin, staff, and hotel portals with granular permission controls' }
  ];

  const benefits = [
    { icon: TrendingUp, title: 'Increase Revenue by 40%', desc: 'Optimize staff utilization and reduce no-shows with smart scheduling' },
    { icon: Clock, title: 'Save 10+ Hours/Week', desc: 'Automate booking management, attendance tracking, and report generation' },
    { icon: Heart, title: 'Delight Your Customers', desc: 'Automated feedback collection and loyalty programs to boost retention' },
    { icon: Smartphone, title: 'Access Anywhere', desc: 'Cloud-based platform accessible from any device, anytime' }
  ];

  const testimonials = [
    {
      name: "Example Wellness",
      role: "Agra & Delhi NCR, 4 Locations",
      quote: "WellNest Technologies has revolutionized our wellness operations. From booking management to staff scheduling, everything runs seamlessly now. Our revenue tracking became effortless!",
      rating: 5,
      website: "examplewellness.com"
    },
    {
      name: "Lhasa Hospitality & Wellness Solutions",
      role: "Lucknow, 2 Locations", 
      quote: "The platform's analytics dashboard gives us insights we never had before. Managing multiple locations from a single dashboard has saved us countless hours every week.",
      rating: 5
    },
    {
      name: "Sunrise Wellness",
      role: "Agra, 3 Locations",
      quote: "From staff attendance tracking to customer feedback collection - everything is streamlined. The WhatsApp integration for booking confirmations has been a game-changer!",
      rating: 5
    }
  ];

  // Team members data - Leadership & Core Team
  const teamMembers = [
    {
      name: "Mukul Sharma",
      role: "CEO & Co-Founder",
      bio: "12+ years in Finance, Education & Operations. AI & ML specialist.",
      linkedin: "https://www.linkedin.com/in/mukulsharma99/",
      image: "https://customer-assets.emergentagent.com/job_38d73c54-2668-44e3-8e25-8ea75b171ade/artifacts/imnsrmzw_1639904029304.jpeg",
      category: "leadership",
      imageStyle: "object-cover object-top scale-110"
    },
    {
      name: "Mohini Kaushik",
      role: "Co-Founder, Design & Planning",
      bio: "Strategic design thinking and business planning expertise.",
      linkedin: "https://www.linkedin.com/in/mohini-kaushik-735690180/",
      image: "https://customer-assets.emergentagent.com/job_879bea89-3066-4cb6-bc4e-81707c2bc7eb/artifacts/eys4vqf7_WhatsApp%20Image%202026-02-23%20at%2015.55.54.jpeg",
      category: "leadership",
      imageStyle: "object-cover object-[center_30%] scale-125"
    },
    {
      name: "Nikhil Sharma",
      role: "Co-Founder, COO",
      bio: "15+ years in hospitality and wellness industry operations.",
      linkedin: null,
      image: null,
      category: "leadership",
      imageStyle: ""
    },
    {
      name: "Vishnu Bharadwaj",
      role: "Operations & Sales",
      bio: "20+ years of business excellence and strategic sales leadership.",
      linkedin: null,
      image: "https://customer-assets.emergentagent.com/job_879bea89-3066-4cb6-bc4e-81707c2bc7eb/artifacts/ccfvedy2_IMG_0412.JPG",
      category: "leadership",
      imageStyle: "object-cover object-top scale-125"
    },
    {
      name: "Kshitiz Chaturvedi",
      role: "Planning, Development & Training",
      bio: "8+ years in tech industry. Expert in training at scale and product development.",
      linkedin: "https://www.linkedin.com/in/kshitiz-chaturvedi-0142b415a/",
      image: "https://customer-assets.emergentagent.com/job_879bea89-3066-4cb6-bc4e-81707c2bc7eb/artifacts/s6e69xnq_WhatsApp%20Image%202026-02-23%20at%2015.24.40.jpeg",
      category: "core",
      imageStyle: "object-cover object-top scale-110"
    },
    {
      name: "Parish Bharadwaj",
      role: "Data Analytics & Statistics",
      bio: "Statistician and data analytics expert driving insights-led decisions.",
      linkedin: "https://www.linkedin.com/in/parish-bharadwaj-6086b3253/",
      image: "https://customer-assets.emergentagent.com/job_879bea89-3066-4cb6-bc4e-81707c2bc7eb/artifacts/mxy32yhk_WhatsApp%20Image%202026-02-23%20at%2015.27.24.jpeg",
      category: "core",
      imageStyle: "object-cover object-[center_15%] scale-[1.8]"
    },
    {
      name: "Nishkarsh Chaturvedi",
      role: "Operations",
      bio: "8+ years of operations and strategic sales leadership.",
      linkedin: "https://www.linkedin.com/in/nishkarsh-chaturvedi/",
      image: "https://customer-assets.emergentagent.com/job_879bea89-3066-4cb6-bc4e-81707c2bc7eb/artifacts/kicwjuez_1771840458609.jpeg",
      category: "core",
      imageStyle: "object-cover object-top scale-110"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-40 bg-slate-950/80 backdrop-blur-xl border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div>
              <span className="text-xl font-bold text-white">WellNest</span>
              <span className="text-xl font-light text-teal-400"> Technologies</span>
            </div>
          </div>
          
          <div className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-slate-300 hover:text-white transition">Features</a>
            <a href="#pricing" className="text-slate-300 hover:text-white transition">Pricing</a>
            <a href="#testimonials" className="text-slate-300 hover:text-white transition">Testimonials</a>
            <a href="#contact" className="text-slate-300 hover:text-white transition">Contact</a>
          </div>
          
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => window.open('/login', '_blank')}
              className="text-slate-300 hover:text-white">
              Sign In
            </Button>
            <Button onClick={() => navigate('/onboarding')}
              className="bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700">
              Start Free Trial
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-teal-500/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-cyan-500/10 rounded-full blur-3xl" />
        </div>
        
        <div className="max-w-6xl mx-auto text-center relative">
          <Badge className="bg-teal-500/20 text-teal-400 border-teal-500/30 mb-6 px-4 py-1">
            <Zap className="w-3 h-3 mr-1" /> {trialDays}-Day Free Trial
          </Badge>
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight tracking-tight">
            The Complete
            <span className="block bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">
              Wellness Business Platform
            </span>
          </h1>
          
          <p className="text-lg text-slate-400 max-w-2xl mx-auto mb-10 leading-relaxed">
            Enterprise-grade SaaS solution for spa chains, wellness centers, and beauty businesses.
            Manage bookings, staff, revenue, and customer relationships — all in one powerful platform.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
            <Button size="lg" onClick={() => navigate('/onboarding')}
              className="bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-lg px-8 h-14">
              Start Your Free Trial <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <a href="https://www.youtube.com/channel/UCTBkMup5qrnyfNokix3qMiQ" target="_blank" rel="noopener noreferrer" data-testid="watch-demo-link">
              <Button size="lg" variant="outline" 
                className="border-slate-600 text-slate-300 hover:bg-slate-800 text-lg px-8 h-14"
                data-testid="watch-demo-btn">
                <Play className="w-5 h-5 mr-2" /> Watch Demo
              </Button>
            </a>
          </div>
          
          <div className="flex flex-wrap items-center justify-center gap-8 text-sm text-slate-500">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-teal-500" />
              <span>No setup fees</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-teal-500" />
              <span>Cancel anytime</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-teal-500" />
              <span>Free data migration</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-teal-500" />
              <span>24/7 support</span>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 px-4 border-y border-slate-800 bg-slate-900/30">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center p-6">
                <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-teal-500/20 to-cyan-500/20 flex items-center justify-center mb-4">
                  <benefit.icon className="w-7 h-7 text-teal-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{benefit.title}</h3>
                <p className="text-sm text-slate-400">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-slate-800 text-slate-300 mb-4">Features</Badge>
            <h2 className="text-4xl font-bold text-white mb-4">
              Everything You Need to Run Your Wellness Business
            </h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              Powerful features designed for modern wellness businesses of all sizes
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <Card key={index} className="bg-slate-800/50 border-slate-700 hover:border-teal-500/50 transition-all group">
                <CardContent className="pt-6">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500/20 to-cyan-500/20 flex items-center justify-center mb-4 group-hover:from-teal-500/30 group-hover:to-cyan-500/30 transition-all">
                    <feature.icon className="w-6 h-6 text-teal-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">{feature.title}</h3>
                  <p className="text-slate-400">{feature.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4 bg-slate-900/50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="bg-slate-800 text-slate-300 mb-4">Pricing</Badge>
            <h2 className="text-4xl font-bold text-white mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-slate-400 text-lg mb-8">
              Choose the plan that fits your business. All plans include a {trialDays}-day free trial.
            </p>
            
            {/* Special Offer Banner */}
            {pricingData?.special_offer?.enabled && pricingData.special_offer.message && (
              <div className="bg-gradient-to-r from-orange-500/20 to-red-500/20 border border-orange-500/30 rounded-xl p-4 mb-8 max-w-xl mx-auto">
                <p className="text-orange-300 font-semibold">{pricingData.special_offer.message}</p>
                <p className="text-orange-400 text-sm mt-1">
                  Extra {pricingData.special_offer.discount_percent}% off on all plans!
                </p>
              </div>
            )}
            
            {/* Monthly/Annual Toggle */}
            <div className="flex items-center justify-center gap-4 mb-8">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-6 py-2 rounded-full font-medium transition-all ${
                  billingCycle === 'monthly'
                    ? 'bg-teal-500 text-white'
                    : 'bg-slate-800 text-slate-400 hover:text-white'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle('annual')}
                className={`px-6 py-2 rounded-full font-medium transition-all relative ${
                  billingCycle === 'annual'
                    ? 'bg-teal-500 text-white'
                    : 'bg-slate-800 text-slate-400 hover:text-white'
                }`}
              >
                Annual
                <span className="absolute -top-2 -right-2 bg-emerald-500 text-white text-xs px-2 py-0.5 rounded-full">
                  Save {pricingData?.annual_discount || 10}%
                </span>
              </button>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {monthlyPlans.slice(0, 3).map((plan, index) => (
              <Card key={plan.id} 
                className={`bg-slate-800/50 border-slate-700 relative ${
                  index === 1 ? 'ring-2 ring-teal-500 scale-105 z-10' : ''
                }`}>
                {index === 1 && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-teal-500 text-white">Most Popular</Badge>
                  </div>
                )}
                <CardHeader>
                  <CardTitle className="text-white">{plan.name}</CardTitle>
                  <div className="mt-4">
                    {getOriginalPrice(plan) > getDisplayPrice(plan) && (
                      <span className="text-slate-500 line-through text-lg mr-2">
                        {formatCurrency(getOriginalPrice(plan))}
                      </span>
                    )}
                    <span className="text-4xl font-bold text-white">{formatCurrency(getDisplayPrice(plan))}</span>
                    <span className="text-slate-400">/{billingCycle === 'annual' ? 'year' : 'month'}</span>
                  </div>
                  {billingCycle === 'annual' && (
                    <p className="text-sm text-emerald-400 mt-2">
                      Save {formatCurrency(plan.amount * 12 - getDisplayPrice(plan))} per year
                    </p>
                  )}
                  {pricingData?.special_offer?.enabled && (
                    <Badge className="mt-2 bg-orange-500/20 text-orange-400">
                      {pricingData.special_offer.discount_percent}% Special Offer Applied
                    </Badge>
                  )}
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3 mb-6">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <Check className="w-5 h-5 text-teal-500 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-300">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button 
                    className={`w-full ${
                      index === 1 
                        ? 'bg-gradient-to-r from-teal-500 to-cyan-600' 
                        : 'bg-slate-700 hover:bg-slate-600'
                    }`}
                    onClick={() => navigate(`/onboarding?plan=${plan.id}&billing=${billingCycle}`)}
                  >
                    Start {trialDays}-Day Free Trial
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <div className="text-center mt-8">
            <p className="text-slate-400">
              Need enterprise features or custom solutions?{' '}
              <a href="#contact" className="text-teal-400 hover:underline">Contact our sales team</a>
            </p>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-slate-800 text-slate-300 mb-4">Testimonials</Badge>
            <h2 className="text-4xl font-bold text-white mb-4">
              Trusted by Leading Wellness Businesses
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="bg-slate-800/50 border-slate-700">
                <CardContent className="pt-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="text-slate-300 mb-6 italic">"{testimonial.quote}"</p>
                  <div>
                    <p className="font-semibold text-white">{testimonial.name}</p>
                    <p className="text-sm text-slate-400">{testimonial.role}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works - Visual Mindmap */}
      <section className="py-20 px-4 bg-slate-900/50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-slate-800 text-slate-300 mb-4">How It Works</Badge>
            <h2 className="text-4xl font-bold text-white mb-4">
              Your Wellness Operations, Simplified
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              One platform to manage everything — from booking to billing, staff to analytics.
            </p>
          </div>
          
          {/* Mindmap Visual */}
          <div className="relative">
            {/* Center Hub */}
            <div className="flex justify-center mb-12">
              <div className="w-40 h-40 rounded-full bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center shadow-lg shadow-teal-500/20">
                <div className="text-center">
                  <Sparkles className="w-8 h-8 text-white mx-auto mb-2" />
                  <span className="text-white font-bold text-sm">WellNest</span>
                  <span className="text-teal-100 text-xs block">Platform</span>
                </div>
              </div>
            </div>
            
            {/* Connected Modules */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center p-6 bg-slate-800/50 rounded-xl border border-slate-700 hover:border-teal-500/50 transition">
                <div className="w-12 h-12 mx-auto rounded-xl bg-blue-500/20 flex items-center justify-center mb-4">
                  <Calendar className="w-6 h-6 text-blue-400" />
                </div>
                <h4 className="font-semibold text-white mb-2">Bookings</h4>
                <p className="text-xs text-slate-400">Schedule, reschedule, package management</p>
              </div>
              
              <div className="text-center p-6 bg-slate-800/50 rounded-xl border border-slate-700 hover:border-teal-500/50 transition">
                <div className="w-12 h-12 mx-auto rounded-xl bg-purple-500/20 flex items-center justify-center mb-4">
                  <Users className="w-6 h-6 text-purple-400" />
                </div>
                <h4 className="font-semibold text-white mb-2">Staff</h4>
                <p className="text-xs text-slate-400">Attendance, leave, performance</p>
              </div>
              
              <div className="text-center p-6 bg-slate-800/50 rounded-xl border border-slate-700 hover:border-teal-500/50 transition">
                <div className="w-12 h-12 mx-auto rounded-xl bg-green-500/20 flex items-center justify-center mb-4">
                  <BarChart3 className="w-6 h-6 text-green-400" />
                </div>
                <h4 className="font-semibold text-white mb-2">Analytics</h4>
                <p className="text-xs text-slate-400">Revenue, trends, forecasting</p>
              </div>
              
              <div className="text-center p-6 bg-slate-800/50 rounded-xl border border-slate-700 hover:border-teal-500/50 transition">
                <div className="w-12 h-12 mx-auto rounded-xl bg-amber-500/20 flex items-center justify-center mb-4">
                  <MessageSquare className="w-6 h-6 text-amber-400" />
                </div>
                <h4 className="font-semibold text-white mb-2">Communication</h4>
                <p className="text-xs text-slate-400">WhatsApp, Email, SMS</p>
              </div>
              
              <div className="text-center p-6 bg-slate-800/50 rounded-xl border border-slate-700 hover:border-teal-500/50 transition">
                <div className="w-12 h-12 mx-auto rounded-xl bg-pink-500/20 flex items-center justify-center mb-4">
                  <Heart className="w-6 h-6 text-pink-400" />
                </div>
                <h4 className="font-semibold text-white mb-2">Customers</h4>
                <p className="text-xs text-slate-400">CRM, feedback, loyalty</p>
              </div>
              
              <div className="text-center p-6 bg-slate-800/50 rounded-xl border border-slate-700 hover:border-teal-500/50 transition">
                <div className="w-12 h-12 mx-auto rounded-xl bg-cyan-500/20 flex items-center justify-center mb-4">
                  <Building2 className="w-6 h-6 text-cyan-400" />
                </div>
                <h4 className="font-semibold text-white mb-2">Multi-Location</h4>
                <p className="text-xs text-slate-400">Chain management, hotel portals</p>
              </div>
              
              <div className="text-center p-6 bg-slate-800/50 rounded-xl border border-slate-700 hover:border-teal-500/50 transition">
                <div className="w-12 h-12 mx-auto rounded-xl bg-red-500/20 flex items-center justify-center mb-4">
                  <Award className="w-6 h-6 text-red-400" />
                </div>
                <h4 className="font-semibold text-white mb-2">Incentives</h4>
                <p className="text-xs text-slate-400">Commissions, bonuses, payouts</p>
              </div>
              
              <div className="text-center p-6 bg-slate-800/50 rounded-xl border border-slate-700 hover:border-teal-500/50 transition">
                <div className="w-12 h-12 mx-auto rounded-xl bg-indigo-500/20 flex items-center justify-center mb-4">
                  <Shield className="w-6 h-6 text-indigo-400" />
                </div>
                <h4 className="font-semibold text-white mb-2">Security</h4>
                <p className="text-xs text-slate-400">Role-based access, data isolation</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section - Elegant Design */}
      <section className="py-24 px-4 bg-gradient-to-b from-slate-950 via-slate-900/50 to-slate-950">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-teal-500/10 text-teal-400 border-teal-500/20 mb-4">Our Team</Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              The Minds Behind <span className="text-teal-400">WellNest</span>
            </h2>
            <p className="text-slate-400 text-lg max-w-3xl mx-auto leading-relaxed">
              A passionate team combining decades of experience in wellness, technology, operations, and business excellence.
            </p>
          </div>
          
          {/* Leadership Row */}
          <div className="mb-12">
            <p className="text-center text-sm font-medium text-teal-400/70 uppercase tracking-wider mb-8">Leadership</p>
            <div className="flex flex-wrap justify-center gap-8 lg:gap-12">
              {teamMembers.filter(m => m.category === 'leadership').map((member, index) => (
                <div key={index} className="group w-48 md:w-56">
                  {/* Photo Container */}
                  <div className="relative mb-5 mx-auto w-36 h-36 md:w-44 md:h-44">
                    {/* Glow effect */}
                    <div className="absolute inset-0 rounded-full bg-gradient-to-br from-teal-500/20 to-cyan-500/20 blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                    
                    {/* Image container */}
                    <div className="relative w-full h-full rounded-full overflow-hidden ring-2 ring-slate-700/50 group-hover:ring-teal-500/50 transition-all duration-500">
                      {member.image ? (
                        <img 
                          src={member.image}
                          alt={member.name}
                          className={`w-full h-full grayscale group-hover:grayscale-0 transition-all duration-700 group-hover:scale-105 ${member.imageStyle || 'object-cover object-center scale-100'}`}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-800 to-slate-900 group-hover:from-slate-700 group-hover:to-slate-800 transition-all duration-500">
                          <span className="text-4xl font-bold text-slate-600 group-hover:text-teal-400 transition-colors duration-500">
                            {member.name.split(' ').map(n => n[0]).join('')}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Info */}
                  <div className="text-center">
                    <h3 className="text-lg font-semibold text-white mb-1 group-hover:text-teal-400 transition-colors duration-300">
                      {member.name}
                    </h3>
                    <p className="text-sm font-medium text-teal-400/80 mb-3">{member.role}</p>
                    <p className="text-sm text-slate-400 leading-relaxed mb-3 min-h-[40px]">{member.bio}</p>
                    
                    {member.linkedin && (
                      <a 
                        href={member.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-[#0077B5] transition-colors duration-300"
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                        </svg>
                        Connect
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Core Team Row */}
          <div>
            <p className="text-center text-sm font-medium text-slate-500 uppercase tracking-wider mb-8">Core Team</p>
            <div className="flex flex-wrap justify-center gap-8 lg:gap-10">
              {teamMembers.filter(m => m.category === 'core').map((member, index) => (
                <div key={index} className="group w-44 md:w-48">
                  {/* Photo Container */}
                  <div className="relative mb-4 mx-auto w-28 h-28 md:w-36 md:h-36">
                    {/* Glow effect */}
                    <div className="absolute inset-0 rounded-full bg-gradient-to-br from-cyan-500/15 to-teal-500/15 blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                    
                    {/* Image container */}
                    <div className="relative w-full h-full rounded-full overflow-hidden ring-2 ring-slate-800 group-hover:ring-cyan-500/40 transition-all duration-500">
                      {member.image ? (
                        <img 
                          src={member.image}
                          alt={member.name}
                          className={`w-full h-full grayscale group-hover:grayscale-0 transition-all duration-700 group-hover:scale-105 ${member.imageStyle || 'object-cover object-center scale-100'}`}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-800 to-slate-900">
                          <span className="text-3xl font-bold text-slate-600 group-hover:text-cyan-400 transition-colors duration-500">
                            {member.name.split(' ').map(n => n[0]).join('')}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Info */}
                  <div className="text-center">
                    <h3 className="text-base font-semibold text-white mb-1 group-hover:text-cyan-400 transition-colors duration-300">
                      {member.name}
                    </h3>
                    <p className="text-xs font-medium text-cyan-400/70 mb-2">{member.role}</p>
                    <p className="text-xs text-slate-400 leading-relaxed mb-2 min-h-[32px]">{member.bio}</p>
                    
                    {member.linkedin && (
                      <a 
                        href={member.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-[#0077B5] transition-colors duration-300"
                      >
                        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                        </svg>
                        Connect
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-teal-900/30 to-cyan-900/30">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Transform Your Wellness Business?
          </h2>
          <p className="text-xl text-slate-300 mb-8">
            Join hundreds of wellness businesses already using WellNest Technologies to grow their revenue and delight their customers.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button size="lg" onClick={() => navigate('/onboarding')}
              className="bg-gradient-to-r from-teal-500 to-cyan-600 text-lg px-12 h-14">
              Start Your {trialDays}-Day Free Trial <ChevronRight className="ml-2" />
            </Button>
            <Button size="lg" variant="outline"
              onClick={() => window.location.href = 'mailto:hello@wellnesttechnologies.com?subject=Sales Inquiry - WellNest Technologies'}
              className="border-slate-500 text-slate-300 hover:bg-slate-800 text-lg px-8 h-14">
              <Headphones className="w-5 h-5 mr-2" /> Talk to Sales
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="py-16 px-4 border-t border-slate-800">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <span className="text-xl font-bold text-white">WellNest</span>
                  <span className="text-xl font-light text-teal-400"> Technologies</span>
                </div>
              </div>
              <p className="text-slate-400 mb-6 max-w-md">
                Enterprise wellness business management platform empowering businesses across India and beyond. 
                Streamline operations, delight customers, grow revenue.
              </p>
              <div className="flex gap-4">
                <a href="https://x.com/WellnestTech" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-700 transition" aria-label="Follow us on X">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                </a>
                <a href="https://www.linkedin.com/in/wellnest-technologies-8241893b3/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-700 transition" aria-label="Follow us on LinkedIn">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg>
                </a>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-3 text-slate-400">
                <li><a href="#features" className="hover:text-white transition">Features</a></li>
                <li><a href="#pricing" className="hover:text-white transition">Pricing</a></li>
                <li><a href="/integrations" className="hover:text-white transition">Integrations</a></li>
                <li><a href="mailto:hello@wellnesttechnologies.com?subject=Work%20With%20Us%20-%20WellNest%20Technologies" className="hover:text-white transition">Work with us</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-4">Contact Us</h4>
              <ul className="space-y-3 text-slate-400">
                <li className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-teal-500" />
                  <span>hello@wellnesttechnologies.com</span>
                </li>
                <li className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-teal-500" />
                  <span>+91 8800977146</span>
                </li>
                <li className="flex items-center gap-3">
                  <Globe className="w-5 h-5 text-teal-500" />
                  <span>www.wellnesttechnologies.com</span>
                </li>
              </ul>
              <div className="mt-6">
                <h5 className="font-medium text-white mb-2">Office</h5>
                <p className="text-slate-400 text-sm">
                  Agra, Uttar Pradesh<br />
                  India
                </p>
              </div>
            </div>
          </div>
          
          <div className="mt-12 pt-8 border-t border-slate-800 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-slate-500 text-sm">
              © 2026 WellNest Technologies Pvt. Ltd. All rights reserved.
            </p>
            <div className="flex gap-6 flex-wrap items-center">
              <a href="/privacy-policy" className="text-slate-500 hover:text-slate-300 text-sm transition">Privacy Policy</a>
              <a href="/terms-of-service" className="text-slate-500 hover:text-slate-300 text-sm transition">Terms of Service</a>
              <a href="/refund-policy" className="text-slate-500 hover:text-slate-300 text-sm transition">Refund Policy</a>
              <span className="text-slate-700">|</span>
              <a href="/wtadmin" className="text-slate-600 hover:text-slate-500 text-xs transition">Admin</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
